import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { NgSelectModule } from '@ng-select/ng-select';
import { DataService } from '../data.service';
import { ENGEDETT_FAJLOK, idoKiir, meretKiir } from './beadando-kozos';

/**
 *  BEADANDÓK – a tanár képernyője (Napló › Beadandók).
 *
 *  Feladat kiadása egy órához (a naplóból a „Beadandó kiadása” gombbal, `?ora=<id>`) vagy egy
 *  tanítási egységhez (osztály/csoport + tantárgy), határidővel és opcionális csatolmánnyal.
 *  A névsor a kiadáskor rögzül. A feladatnál látszik, ki adta be, mikor, késve-e, és ki nem;
 *  beadásonként szöveges visszajelzés és szöveges értékelés írható. Jegyet NEM ír: a jegy a
 *  napló Értékelés képernyőjén kerül be (backend/beadando.js).
 */
@Component({
  selector: 'app-beadandok',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgSelectModule],
  templateUrl: './beadandok.component.html',
  styleUrls: ['./beadandok.component.scss'],
})
export class BeadandokComponent implements OnInit {
  intezmeny = '';
  toltodik = true;
  hiba = '';
  feladatok: any[] = [];
  egysegek: any[] = [];

  /*  Új feladat */
  ujNyitva = false;
  ujOra: any = null;             // ha a naplóból jött: az óra adatai
  ujEgyseg = '';                 // egység-kulcs (ha nem órához) – tartalék, ha a tanított lista nem jön
  /*  KÜLÖN OSZTÁLY/CSOPORT ÉS TANTÁRGY VÁLASZTÓ (2026-10-19, a user: „mint az értékeléseknél, az
   *  automata kitöltéssel és szűréssel”). A forrás ugyanaz, mint az Értékeléseké (`/api/tanitott`,
   *  tanitott.js): a pedagógus a saját (és helyettesített) osztályait/csoportjait kapja, üres nélkül;
   *  a tantárgy az egység órarendi tárgyaiból választható, és ha egyetlen ilyen van, kitöltődik. */
  tanitott: any[] | null = null; // null = nem jött meg → tartalék: a régi összevont választó
  egysegbenTantargyak: string[] = [];
  tantargyNev = new Map<string, string>();
  ujTetel: string | null = null;  // a tétel `ertek` mezője (o:<id> / c:<id>)
  ujTantargy: string | null = null;
  tantargyValasztek: { id: string; name: string }[] = [];
  ujCim = '';
  ujLeiras = '';
  ujHatarido = '';
  ujKeso = true;
  ujFajl: File | null = null;
  kiad = false;
  readonly fajlTipusok = ENGEDETT_FAJLOK;

  /*  Kiválasztott feladat */
  reszlet: any = null;
  reszletToltodik = false;
  szerkHatarido = '';
  szerkKeso = true;
  vjMentes: Record<string, boolean> = {};

  readonly idoKiir = idoKiir;
  readonly meretKiir = meretKiir;

  constructor(private http: HttpClient, private data: DataService, private route: ActivatedRoute,
              private router: Router, private alert: AlertController) {}

  async ngOnInit() {
    const sajat = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(sajat?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    if (!this.intezmeny) { this.hiba = 'Nem azonosítható az intézmény.'; this.toltodik = false; return; }
    this.ujHatarido = this.alapHatarido();
    const p = this.route.snapshot.queryParamMap;
    await Promise.all([this.listaBetolt(), this.egysegBetolt(), this.tanitottBetolt()]);
    if (p.get('ora')) await this.oraBol(String(p.get('ora')));
    if (p.get('feladat')) await this.megnyit(String(p.get('feladat')));
  }

  /** Egy hét múlva, este 8 – a datetime-local mező helyi idős alakjában. */
  private alapHatarido(): string {
    const d = new Date(Date.now() + 7 * 86400000);
    const k = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${k(d.getMonth() + 1)}-${k(d.getDate())}T20:00`;
  }

  async listaBetolt() {
    this.toltodik = true;
    try {
      this.feladatok = await firstValueFrom(this.http.get<any[]>(`/api/beadando/feladatok/${this.intezmeny}`)) || [];
      this.hiba = '';
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A feladatok nem tölthetők be.';
    } finally {
      this.toltodik = false;
    }
  }

  async egysegBetolt() {
    try {
      this.egysegek = await firstValueFrom(this.http.get<any[]>(`/api/beadando/egysegek/${this.intezmeny}`)) || [];
    } catch { this.egysegek = []; }
  }

  /** A tanév kezdőéve (a fejléc munkatanéve szerint) – a tanított lista ehhez kéri. */
  private async tanevEv(): Promise<number | null> {
    try {
      const adatok = JSON.parse(await this.data.getFromStorage('tanevadatok')) || [];
      const munka = await this.data.getMunkatanev();
      const akt = await this.data.getAktualisTanev();
      const t = adatok.find((d: any) => d.id == munka) || adatok.find((d: any) => d.id == akt);
      const ev = Number(String(t?.tankezd || '').slice(0, 4));
      return ev || null;
    } catch { return null; }
  }

  async tanitottBetolt() {
    const ev = await this.tanevEv();
    if (!ev) { this.tanitott = null; return; }
    try {
      const [t, targyak]: any[] = await Promise.all([
        firstValueFrom(this.http.get(`/api/tanitott/${this.intezmeny}?ev=${ev}`)),
        firstValueFrom(this.data.getTantargy(ev, this.intezmeny)).catch(() => []),
      ]);
      this.tantargyNev = new Map(((targyak || []) as any[]).map((x: any) => [String(x.id), String(x.name || x.nev || '')]));
      this.egysegbenTantargyak = ((t?.egysegben_tantargyak || []) as any[]).map(String);
      this.tanitott = ((t?.tetelek || []) as any[]);
    } catch { this.tanitott = null; }
  }

  /** Osztály/csoport választás: a tantárgylista szűkül, egyetlen tárgynál kitöltődik. */
  tetelValasztas() {
    const t = (this.tanitott || []).find((x) => x.ertek === this.ujTetel);
    const ids: string[] = ((t?.tantargyak || []) as any[]).map(String);
    const forras = ids.length ? ids : this.egysegbenTantargyak;
    this.tantargyValasztek = forras
      .map((id) => ({ id, name: this.tantargyNev.get(id) || '' }))
      .filter((x) => x.name)
      .sort((a, b) => a.name.localeCompare(b.name, 'hu'));
    const javaslat = t?.tantargy ? String(t.tantargy) : (ids.length === 1 ? ids[0] : null);
    if (javaslat && this.tantargyValasztek.some((x) => x.id === javaslat)) this.ujTantargy = javaslat;
    else if (!this.tantargyValasztek.some((x) => x.id === this.ujTantargy)) this.ujTantargy = null;
  }

  private async oraBol(oraId: string) {
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/beadando/ora/${encodeURIComponent(oraId)}`));
      if (!r?.szerkesztheto) { this.data.sendToast('Ehhez az órához az óra tartója vagy helyettesítője adhat feladatot.', 'warning'); return; }
      this.ujOra = r.ora;
      this.ujNyitva = true;
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az óra nem tölthető be.', 'error');
    }
  }

  oraLeiras(o: any): string {
    if (!o) return '';
    const vege = Number(o.hossz) > 1 ? `–${Number(o.ora) + Number(o.hossz) - 1}` : '';
    return `${String(o.datum || '').replace(/-/g, '. ')}. ${o.ora}${vege}. óra · ${o.egyseg || '–'} · ${o.tantargy || '–'}`;
  }

  oraElvetes() { this.ujOra = null; this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true }); }

  fajlValasztas(ev: any) {
    const f: File | undefined = ev?.target?.files?.[0];
    if (f && f.size > 20 * 1024 * 1024) { this.data.sendToast('A csatolmány legfeljebb 20 MB lehet.', 'warning'); ev.target.value = ''; return; }
    this.ujFajl = f || null;
  }

  async kiadas(fajlInput?: HTMLInputElement) {
    if (this.kiad) return;
    if (!this.ujCim.trim()) { this.data.sendToast('A feladat címe kötelező.', 'warning'); return; }
    if (!this.ujHatarido) { this.data.sendToast('Adj meg határidőt.', 'warning'); return; }
    const fd = new FormData();
    fd.append('intezmeny', this.intezmeny);
    if (this.ujOra?.id) {
      fd.append('ora_id', this.ujOra.id);
    } else {
      if (this.tanitott) {
        const t = this.tanitott.find((x) => x.ertek === this.ujTetel);
        if (!t || !this.ujTantargy) { this.data.sendToast('Válaszd ki az osztályt/csoportot és a tantárgyat.', 'warning'); return; }
        if (t.tipus === 'csoport') fd.append('csoport', t.csoport); else fd.append('osztaly', t.id);
        fd.append('tantargy', this.ujTantargy);
      } else {
        const e = this.egysegek.find((x) => x.kulcs === this.ujEgyseg);
        if (!e) { this.data.sendToast('Válaszd ki az osztályt/csoportot és a tantárgyat.', 'warning'); return; }
        if (e.csoport) fd.append('csoport', e.csoport); else fd.append('osztaly', e.osztaly);
        fd.append('tantargy', e.tantargy);
      }
    }
    fd.append('cim', this.ujCim.trim());
    fd.append('leiras', this.ujLeiras || '');
    fd.append('hatarido', this.ujHatarido);
    fd.append('keso_beadas', this.ujKeso ? '1' : '0');
    if (this.ujFajl) fd.append('csatolmany', this.ujFajl, this.ujFajl.name);
    this.kiad = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/beadando/feladat', fd));
      this.data.sendToast(`A feladat kiadva ${r?.cimzett_db || 0} tanulónak.`);
      this.ujCim = ''; this.ujLeiras = ''; this.ujFajl = null; this.ujHatarido = this.alapHatarido(); this.ujKeso = true;
      if (fajlInput) fajlInput.value = '';
      this.ujNyitva = false;
      if (this.ujOra) this.oraElvetes();
      await this.listaBetolt();
      if (r?.id) await this.megnyit(r.id);
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A feladat nem adható ki.', 'error');
    } finally {
      this.kiad = false;
    }
  }

  async megnyit(id: string) {
    this.reszletToltodik = true;
    try {
      this.reszlet = await firstValueFrom(this.http.get(`/api/beadando/feladat/${encodeURIComponent(id)}`));
      for (const t of this.reszlet?.tanulok || []) {
        t.vj = t.beadas?.visszajelzes || '';
        t.ert = t.beadas?.ertekeles || '';
      }
      this.szerkHatarido = String(this.reszlet?.feladat?.hatarido || '').slice(0, 16).replace(' ', 'T');
      this.szerkKeso = !!this.reszlet?.feladat?.keso_beadas;
    } catch (e: any) {
      this.reszlet = null;
      this.data.sendToast(e?.error?.error || 'A feladat nem tölthető be.', 'error');
    } finally {
      this.reszletToltodik = false;
    }
  }

  bezar() { this.reszlet = null; }

  get beadottDb(): number { return (this.reszlet?.tanulok || []).filter((t: any) => t.beadas?.beadva).length; }

  allapot(t: any): { szoveg: string; szin: string } {
    const b = t.beadas;
    if (!b?.beadva) return { szoveg: 'Nincs beadva', szin: 'medium' };
    if (b.keso) return { szoveg: 'Késve beadva', szin: 'warning' };
    return { szoveg: 'Beadva', szin: 'success' };
  }

  async feladatMentes() {
    const f = this.reszlet?.feladat;
    if (!f) return;
    try {
      await firstValueFrom(this.http.put(`/api/beadando/feladat/${f.id}`, { hatarido: this.szerkHatarido, keso_beadas: this.szerkKeso }));
      this.data.sendToast('A feladat módosítva.');
      await Promise.all([this.megnyit(f.id), this.listaBetolt()]);
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A módosítás nem menthető.', 'error');
    }
  }

  async feladatTorles() {
    const f = this.reszlet?.feladat;
    if (!f) return;
    const a = await this.alert.create({
      header: 'Feladat törlése',
      message: `Biztosan törlöd a(z) „${f.cim}” feladatot? A tanulók többé nem látják.`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive' }],
    });
    await a.present();
    const { role } = await a.onDidDismiss();
    if (role !== 'destructive') return;
    try {
      await firstValueFrom(this.http.delete(`/api/beadando/feladat/${f.id}`));
      this.reszlet = null;
      this.data.sendToast('A feladat törölve.');
      await this.listaBetolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A feladat nem törölhető.', 'error');
    }
  }

  async visszajelzes(t: any) {
    if (!t.beadas?.id) return;
    this.vjMentes[t.oktazon] = true;
    try {
      const r: any = await firstValueFrom(this.http.put(`/api/beadando/beadas/${t.beadas.id}/visszajelzes`,
        { visszajelzes: t.vj || '', ertekeles: t.ert || '' }));
      t.beadas.visszajelzes = t.vj || null;
      t.beadas.ertekeles = t.ert || null;
      t.beadas.visszajelezve = r?.torolve ? null : r?.visszajelezve;
      t.beadas.modositva_visszajelzes_utan = false;
      this.data.sendToast(r?.torolve ? 'A visszajelzés törölve.' : 'Visszajelzés elküldve.');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A visszajelzés nem menthető.', 'error');
    } finally {
      this.vjMentes[t.oktazon] = false;
    }
  }

  fajlMegnyit(t: any, fajl: any) {
    this.data.megnyitFajl(`/api/beadando/tanulo/${encodeURIComponent(t.oktazon)}/fajl/${fajl.id}`, fajl.fajlnev);
  }

  csatolmanyMegnyit() {
    const f = this.reszlet?.feladat;
    if (f?.csatolmany) this.data.megnyitFajl(`/api/beadando/feladat/${f.id}/csatolmany`, f.csatolmany.nev);
  }
}
